"use client";

import * as api from "@/src/api/sdk.gen";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { User as UserIcon } from "lucide-react";

type SaveState = { status: "idle" } | { status: "saving" } | { status: "saved" } | { status: "error"; message: string };

export default function ProfileNameCard() {
    const queryClient = useQueryClient();
    const { data: profile, isLoading } = useQuery({
        queryKey: ["user-profile"],
        queryFn: async () => {
            const { data } = await api.getCurrentUserProfileApiV1UsersMeGet();
            return data ?? null;
        },
    });

    const [name, setName] = useState("");
    const [saveState, setSaveState] = useState<SaveState>({ status: "idle" });

    useEffect(() => {
        setName(profile?.name ?? "");
    }, [profile?.name]);

    const hasChanges = name.trim() !== (profile?.name ?? "");

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setSaveState({ status: "error", message: "Name can't be empty." });
            return;
        }
        setSaveState({ status: "saving" });
        try {
            const { error } = await api.updateUserTourStatusApiV1UsersMePatch({
                body: { name: trimmed },
            });
            if (error) throw new Error("Failed to save name");
            await queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            setSaveState({ status: "saved" });
        } catch {
            setSaveState({ status: "error", message: "Could not save your name. Please try again." });
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm">
                <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
            </div>
        );
    }

    return (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <UserIcon className="h-4 w-4" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Your Name</h2>
                    <p className="text-sm text-slate-600">Used on certificates and displayed on your profile.</p>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (saveState.status !== "idle") setSaveState({ status: "idle" });
                    }}
                    placeholder="Enter your full name"
                    maxLength={255}
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!hasChanges || saveState.status === "saving"}
                    className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saveState.status === "saving" ? "Saving..." : "Save"}
                </button>
            </div>

            {saveState.status === "saved" && (
                <p className="mt-2 text-sm text-emerald-600">Saved.</p>
            )}
            {saveState.status === "error" && (
                <p className="mt-2 text-sm text-red-600">{saveState.message}</p>
            )}
        </div>
    );
}
